import { Component, OnInit, ViewChild, AfterViewInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { HelpLink, ScriptConfiguration, ScriptingLanguageType, ScriptingProperty } from './cpq-scripting.model';
import { ScriptingService } from './cpq-scripting.service';
import '../../../../node_modules/codemirror/lib/codemirror';
import '../../../../node_modules/codemirror/mode/python/python';
import '../../../../node_modules/codemirror/mode/htmlmixed/htmlmixed';
import '../../../../node_modules/codemirror/addon/hint/show-hint';
import '../../../../node_modules/codemirror/addon/display/fullscreen';
import '../../../../node_modules/codemirror/addon/merge/merge';
import '../../../../node_modules/codemirror/addon/edit/trailingspace';
import { TranslationService } from '../common/services/translation.service';
import { ILabel } from '../ILabel.model';
import { navigateToOldSetup } from '../common/functions/common-helpers';
import { SegregationOfDutiesService } from '../../users-module/segregation-of-duties/segregation-of-duties.service';
import { CurrentUser } from '../common/services/current-user.service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../dialogs/alert-dialog/alert-dialog.component';
import { CpqCodemirrorComponent } from '../cpq-codemirror/cpq-codemirror.component';
import { TenantSettings } from '../common/services/tenant-settings.service';



@Component({
    selector: 'cpq-scripting-codemirror',
    templateUrl: './cpq-scripting-codemirror.component.html',
    styleUrls: ['./cpq-scripting-codemirror.component.scss'],
    providers: [ScriptingService, SegregationOfDutiesService],
    standalone: false
})
export class CpqScriptingCodemirrorComponent implements OnInit, OnChanges {
  @Input('script') script: ScriptConfiguration;
  @Input('disabled') disabled: boolean;
  @Input('label') label: ILabel;
  @Input() readFromModel: { model: any, propName: string, disabled?: boolean };
  @Input('languageType') languageType: ScriptingLanguageType = ScriptingLanguageType.IronPython;

  @Input() hideWorkbenchLink: boolean;
  @Input() hideProxyGenerationToggle: boolean;
  @Input() hideFooter: boolean;
  @Input() styleActiveLine: boolean = false;
  @Input() lineNumbers: boolean = true;
  @Input() showLaunchVSCodeButton: boolean = false;
  @Output() onModelChange: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('editor') editor: CpqCodemirrorComponent;

  public config;
  public propertiesVisible = false;
  public forceProxyGeneration = true;
  public firstLevelProperties: Array<ScriptingProperty>;
  public containsErrors: boolean;
  public errorMessage: string;
  public errorStatus: string;
  public showCodeMirrorEditor:boolean = false;
  public canAccessWorkBench = true;
  public vsCodePluginLink: string;

  constructor(private scriptingService: ScriptingService,
    private translationService: TranslationService,
    private segregationOfDutiesService: SegregationOfDutiesService,
    private currentUser: CurrentUser,
    public dialog: MatDialog,
    public tenantSettings: TenantSettings) {
  }

  ngOnChanges() {
    // TODO needs to be refactored in future
    // refresh is needed in case codemirror is inside a tab or a modal
    setTimeout(() => {

      this.refreshInstance();
    }, 1000);
  }

  ngOnInit() {
    this.vsCodePluginLink = this.tenantSettings.vsCodeScriptingExtensionUrl;
    this.initConfiguration();
    this.firstLevelProperties = new Array<ScriptingProperty>();
    this.segregationOfDutiesService.canAccessPage("/ScriptWorkbench").subscribe(result => {
      this.canAccessWorkBench = result;
      });
  }

  checkSyntax() {
    let script: string;
    if (this.readFromModel) {
      script = this.readFromModel.model[this.readFromModel.propName];
    } else {
      script = this.script.script;
    }

    if (script !== '') {
      this.scriptingService.CheckSyntax(new ScriptConfiguration(script)).subscribe(result => {
        this.handleCheckSyntax(result);
      });
    } else {
      this.errorMessage = '';
      this.errorStatus = '';
      this.containsErrors = false;
    }
  }

  handleCheckSyntax(result: string) {
    if (result) {
      this.errorMessage = result;
      this.errorStatus = this.translationService.translate('Admin.modifyitemattributes.LabelDefaultErrMsg');
      this.containsErrors = true;
    } else {
      this.errorMessage = '';
      this.errorStatus = this.translationService.translate('Msg.OK');
      this.containsErrors = false;
    }
  }

  handleCheckSyntaxWarningDialog(result: string) {
    if (result && !this.tenantSettings.forbidSavingInvalidResponsiveTemplates) {
      this.dialog.open(AlertDialogComponent, { data: { title: "Razor validation", content: result, isHtml: true} }).afterOpened().subscribe(response => {
        this.setHelpLink();
      });;
    }
  }

  navigateToScriptWorkbench() {
    if(this.canAccessWorkBench)
    {
      var tenant = this.currentUser.domain;
      let script: string;
      if (this.readFromModel) {
        script = this.readFromModel.model[this.readFromModel.propName];
      } else {
        script = this.script.script;
      }
      localStorage.setItem(tenant + '.scriptDebugger.testScript', script);
      localStorage.setItem(tenant + '.scriptDebugger.mode', 'standard');
      navigateToOldSetup('../ScriptWorkbench', true);
    }
  }

  private initConfiguration(){
    this.config = {
      mode: {
        name: this.resolveLanguageType(),
        version: 3,
        singleLineStringErrors: false
      },
      lineNumbers: this.lineNumbers,
      indentUnit: 4,
      tabMode: 'shift',
      matchBrackets: true,
      showTrailingSpace: true,
      lineWrapping: true,
      readOnly: this.disabled,
      styleActiveLine: this.styleActiveLine,
      extraKeys: {
        'Ctrl-Space': 'autocomplete',
        'F11': function (cm) {
          cm.setOption('fullScreen', !cm.getOption('fullScreen'));
        },
        'Esc': function (cm) {
          if (cm.getOption('fullScreen')) {
            cm.setOption('fullScreen', false);
          }
        }
      }
    };
  }

  private resolveLanguageType(): string{
    switch(this.languageType){
      case ScriptingLanguageType.IronPython:
        return 'python';
      case ScriptingLanguageType.Html:
        return 'htmlmixed';
      case ScriptingLanguageType.TypeScript:
        return 'text/typescript';
    }
  }

  public showScriptingMethodsList(){

    if (this.languageType == ScriptingLanguageType.IronPython && this.firstLevelProperties.length === 0) {
      this.scriptingService.GetFirstLevelProperties('standard').subscribe(firstLevelProperties => {
        this.firstLevelProperties = firstLevelProperties;
        this.firstLevelProperties.forEach(property => {
          property.isCollapsed = true;
          property.propertiesAndFunctions = [];
        });

      this.propertiesVisible = !this.propertiesVisible;
      });
    }else {
            this.propertiesVisible = !this.propertiesVisible;
    }

  }

  public refreshInstance() {
    this.editor.instance.refresh();
  }

  private setHelpLink() {
    let dialogContent = document.getElementsByClassName("cald_content")[0];
    if (dialogContent) {
      dialogContent.getElementsByTagName("a")[0].href = HelpLink.RzorValidation;
    }
  }
}
